/**
 * File Upload Control
 * File upload input field with multiple file support
 * Performance: Memoized to prevent unnecessary re-renders
 */

import React, { useCallback, useMemo, memo } from 'react';
import { WarningIcon } from '@atlas-forms/ui-components-react';
import type { ControlRendererProps } from '../ControlRenderer';
import { getBaseControlClassName } from '../utils/classNameUtils';
import { useFormTheme } from '../../contexts/FormThemeContext';
import '../../styles/file-upload.css';

const FileUploadControl = memo(React.forwardRef<
  HTMLInputElement,
  ControlRendererProps
>(
  (
    {
      control,
      value,
      onChange,
      onBlur,
      error,
      touched,
      disabled,
    },
    ref,
  ) => {
    const theme = useFormTheme();
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
          // Support both single and multiple file uploads
          const multiple = control.config?.multiple === true;
          if (multiple) {
            onChange(Array.from(files));
          } else {
            onChange(files[0]);
          }
        }
      },
      [control.config, onChange],
    );

    const showError = touched && error;
    const containerClassName = getBaseControlClassName(control, {
      error: showError,
      disabled: disabled || control.disabled,
    });

    // Get file upload configuration
    const multiple = (control.config?.multiple as boolean) || false;
    const accept = (control.config?.accept as string) || '';
    const maxSize = (control.config?.maxSize as number) || undefined;

    // Format file size for display
    const formatFileSize = (bytes: number): string => {
      const units = ['B', 'KB', 'MB', 'GB'];
      let size = bytes;
      let unitIndex = 0;
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }
      return `${size.toFixed(2)} ${units[unitIndex]}`;
    };

    // Get display value (list of selected files)
    const displayValue = useMemo(() => {
      if (!value) return '';
      if (Array.isArray(value)) {
        return value.map((f) => (f as File).name).join(', ');
      }
      if (value instanceof File) {
        return value.name;
      }
      return '';
    }, [value]);

    return (
      <div className={containerClassName}>
        {control.label && (
          <label htmlFor={control.id} className="control__label">
            <span className="control__label-text">{control.label}</span>
            {control.required && (
              <span className="control__required" aria-label="required">
                *
              </span>
            )}
          </label>
        )}

        <div className={`control__file-upload-wrapper control__file-upload-wrapper--${theme}`}>
          <input
            ref={ref}
            id={control.id}
            type="file"
            name={control.id}
            onChange={handleChange}
            onBlur={onBlur}
            disabled={disabled || control.disabled}
            multiple={multiple}
            accept={accept}
            className="control__input control__input--file"
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={`${control.id}-error ${control.id}-help`}
          />

          <label
            htmlFor={control.id}
            className={`control__file-upload-label control__file-upload-label--${theme}${showError ? ' control__file-upload-label--error' : ''}`}
            aria-disabled={disabled || control.disabled}
          >
            <span className="control__file-upload-text">
              {control.placeholder || '📁 Choose file(s) or drag and drop'}
            </span>
          </label>

          {displayValue && (
            <div className={`control__file-upload-selected control__file-upload-selected--${theme}`}>
              <span className="control__file-upload-selected-label">
                ✓ Selected
              </span>
              <span className="control__file-upload-selected-files">
                {displayValue}
              </span>
              {showError && (
                <span className="control__error-icon" aria-hidden="true" title="Error">
                  <WarningIcon width={16} height={16} />
                </span>
              )}
            </div>
          )}
        </div>

        {maxSize && (
          <div className="control__help">
            Max file size: {formatFileSize(maxSize)}
          </div>
        )}

        {control.helpText && (
          <div id={`${control.id}-help`} className="control__help">
            {control.helpText}
          </div>
        )}

        {showError && (
          <div
            id={`${control.id}-error`}
            className="control__error-message"
            role="alert"
          >
            {error}
          </div>
        )}
      </div>
    );
  },
));

FileUploadControl.displayName = 'FileUploadControl';

export default FileUploadControl;
